import React, {
 useEffect, useState, useCallback, useRef
} from "react"
import { Link, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import dayjs from "dayjs"
import ReactQuill from "react-quill-new"
import { ReactTags } from "react-tag-autocomplete"
import {
  FaRegCopy, FaEye, FaSave, FaChartBar, FaCalculator, FaHeartbeat,
  FaUsers, FaHistory, FaInfoCircle, FaClipboardList, FaBolt, FaDownload,
  FaExternalLinkAlt, FaLink, FaPlug, FaChevronRight, FaCheckCircle
} from "react-icons/fa"

import {
  getProgramDetail, getProgramStats, updateProgramDetail
} from "../../requesters/Backend/ProgramRequester"

import { getTagsForProgram, saveTag } from "../../requesters/Backend/TagRequester"

import { getLearnWorldProduct } from "../../requesters/Backend/LearnWorldProductRequester"
import {
  exportUsersCsv, getTemplates, setSelectedPulseSurvey, createReminders
} from "../../requesters/Backend/CalculatorRequester"
import { calculatorTypeLabel, convertSnakeToTitle } from "../../util"
import { ALLOWED_CALCULATOR_TYPES } from "../Dashboard/constants"
import SwitchWithInflight from "./Programs/SwitchWithInflight"
// eslint-disable-next-line no-unused-vars
import Reminder from "./Programs/Reminder"

// Rich text editor config for the program Body field (matches the snow theme toolbar).
const BODY_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
}

const BODY_EDITOR_FORMATS = [
  "header",
  "bold", "italic", "underline",
  "list",
  "link",
]

// Read-only value with a copy button (Unique Code / Unique Sign Up URL).
function CopyableField({
 label, value, variant = "", onCopy
}) {
  return (
    <div className={`form-group copy-field ${variant}`}>
      <div className="copy-field-label">{label}</div>
      <div className="copy-field-value">
        <span className="copy-field-text">{value}</span>
        {value ? (
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm copy-field-btn"
            title={`Copy ${label}`}
            onClick={() => onCopy(value, label)}>
            <FaRegCopy />
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ProgramDetailPage({ baseUrl, currentUser }) {
  const today = dayjs().format("YYYY-MM-DD")
  const { programId } = useParams()
  const [form, setForm] = useState([])
  const [changesDetected, setChangesDetected] = useState(false)
  const [programDetail, setProgramDetail] = useState({})
  const [learnWorldProducts, setLearnWorldProducts] = useState([])
  const [customTemplates, setCustomTemplates] = useState([])
  const [selectedCustomTemplate, setSelectedCustomTemplate] = useState({})
  const [selectedTag, setSelectedTag] = useState([])
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState(false)
  const [calculatorPublishDate, setCalculatorPublishDate] = useState("")
  const [startDate, setStartDate] = useState("")
  const [integrationType, setIntegrationType] = useState("")
  const [endDate, setEndDate] = useState(today)
  const [reminderList, setReminderList] = useState([])
  const [calculatorStats, setCalculatorStats] = useState([])
  const minDate = dayjs(programDetail?.created_at).format("YYYY-MM-DD")
  const [suggestions, setSuggestions] = useState([])
  const [isBusy, setIsBusy] = useState(false)
  const [isLoadingProgram, setIsLoadingProgram] = useState(true)

  const debounceTimeoutRef = useRef(null)

  const copyToClipboard = (value, label) => {
    if (!value) return
    navigator.clipboard.writeText(value)
      .then(() => toast.success(`${label} copied to clipboard`))
      .catch(() => toast.error(`Failed to copy ${label}`))
  }

  const fetchProgramDetail = useCallback(() => {
    setIsLoadingProgram(true)
    getProgramDetail(baseUrl, programId, currentUser)
      .then(res => {
        const program = res.response?.json?.program
        if (program) {
          setForm(program?.socio_question_set?.questions_status)
          setProgramDetail(program)
          setSelectedCustomTemplate(program?.current_pulse_check)
          setReminderList(program?.current_pulse_check?.reminders || [])
          setCalculatorPublishDate(program?.current_pulse_check?.published_date)
          setStartDate(dayjs(program?.created_at).format("YYYY-MM-DD"))
          setIntegrationType(program?.integration_type)
          setSelectedTag(program?.tags)
        }
        return null
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log(typeof err, Object.keys(err))
        toast.error(`Program not found: ${err.errors[0].detail}`)
      })
      .finally(() => setIsLoadingProgram(false))
  }, [baseUrl, programId, currentUser])

  const fetchLearnWorldProduct = useCallback(() => {
    getLearnWorldProduct(baseUrl, currentUser)
      .then(res => {
        const products = res.response?.json?.products
        if (products) {
          setLearnWorldProducts(products)
        }
        return null
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log(typeof err, Object.keys(err))
        toast.error(`Learn World Products not found: ${err.errors[0].detail}`)
      })
  }, [baseUrl, currentUser])

  const fetchProgramStats = useCallback((startDate, endDate) => {
    const params = { program_id: programId, start_date: startDate, end_date: endDate }
    getProgramStats(baseUrl, params, currentUser)
      .then(res => {
        const program = res.response?.json?.program
        if (program) {
          setCalculatorStats(program)
        }
        return null
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.log(typeof err, Object.keys(err))
        toast.error(`Program not found: ${err.errors[0].detail}`)
      })
  }, [baseUrl, currentUser, programId])

  const fetchTemplates = useCallback(() => {
    getTemplates(baseUrl, currentUser).then(res => {
      setCustomTemplates(res.response.json.calculator)
      return null
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.log(err)
    })
  }, [baseUrl, currentUser])

  const fetchTags = useCallback(value => {
    try {
      const query = encodeURIComponent(value)
      const response = getTagsForProgram(baseUrl, query, currentUser)

      return response.then(res => res.response.json.tags)
    } catch (error) {
      return []
    }
  }, [baseUrl, currentUser])

  useEffect(() => {
    fetchProgramDetail()
    fetchTemplates()
    fetchLearnWorldProduct()
  }, [fetchProgramDetail, fetchTemplates, fetchLearnWorldProduct])

  useEffect(() => {
    if (endDate && startDate && endDate >= startDate) {
      const start = dayjs(startDate).toISOString()
      const end = dayjs(endDate).endOf("day").toISOString()
      fetchProgramStats(start, end)
    } else if (endDate && startDate && endDate < startDate) {
      toast.error("End date should be greater than start date")
    }
  }, [startDate, endDate, fetchProgramStats])

  function onSubmit() {
    const payload = { ...programDetail }
    if (integrationType === "learn_world" && !programDetail.offer_redirect_url?.trim()) {
      toast.error("Offer Redirect Url is required for the Learn Worlds integration")
      return
    }
    if (changesDetected) {
      payload.active = form.map(item => item.active && item.label)
      payload.inactive = form.map(item => !item.active && item.label)
      payload.tags = selectedTag.map(tag => tag.value)

      updateProgramDetail(baseUrl, payload, currentUser).then(res => {
        if (res.response.status === "ok") {
          // window.location.href = `/backend/units/${programDetail.unit_id}`
          toast.success("Program updated successfully")
          // window.location.reload()
          fetchProgramDetail()
          setChangesDetected(false)
        }
      })
    }
    if (selectedCustomTemplate && isUpdatingTemplate) {
      const form = { ...selectedCustomTemplate, program_id: programId, new_published_date: calculatorPublishDate }
      setSelectedPulseSurvey(baseUrl, form, currentUser)
        .then(res => {
          const selectedCalculatorID = res?.response?.json?.calculator_id
          if (selectedCalculatorID && reminderList?.length) {
            const form = { reminder_data: reminderList, calculator_id: selectedCalculatorID }
            createReminders(baseUrl, form, currentUser)
          }
        })
        .catch(err => {
          toast.error(`Something went wrong: ${err?.request?.responseText}`)
        })
    }
  }

  const onSaveTag = useCallback(tag => {
    saveTag(baseUrl, tag, currentUser).then(() => {})
  }, [baseUrl, currentUser])

  const onUpdatePulseCalculator = e => {
    const selectedTemplateName = e.target.value
    const selectedTemplate = customTemplates.find(template => template.name === selectedTemplateName)
    setSelectedCustomTemplate({ ...selectedTemplate, published_date: dayjs().format("YYYY-MM-DD") })
    setCalculatorPublishDate(dayjs().format("YYYY-MM-DD"))
    setChangesDetected(true)
    setIsUpdatingTemplate(true)
  }

  const onUpdatePublishDate = e => {
    const selectedPublishDate = e.target.value
    setCalculatorPublishDate(selectedPublishDate)
    setIsUpdatingTemplate(true)
    setChangesDetected(true)
  }

  const onChange = e => {
    if (e.target.name === "integration_type") {
      setIntegrationType(e.target.value)
    }
    if (e.target.name === "learn_world_product_id") {
      const selectedProduct = learnWorldProducts
        .flatMap(group => group.products)
        .find(product => product.product_id === e.target.value)
      setChangesDetected(true)
      setProgramDetail({
        ...programDetail,
        learn_world_product_id: e.target.value,
        offer_redirect_url: selectedProduct?.redirect_url || ""
      })
      return
    }
    setChangesDetected(true)
    setProgramDetail({ ...programDetail, [e.target.name]: e.target.value })
  }

  // ReactQuill hands back an HTML string rather than a DOM event. Quill emits
  // "<p><br></p>" for an empty editor, so normalise that back to an empty string.
  const onBodyChange = value => {
    const normalised = value === "<p><br></p>" ? "" : value
    setChangesDetected(true)
    setProgramDetail({ ...programDetail, body: normalised })
  }

  const onSwitchClick = e => {
    setChangesDetected(true)
    const newForm = [...form]
    newForm[e.target.name].active = e.target.checked
    setForm(newForm)
  }

  const DEFAULT_REMINDER = {
    title: "",
    body: "",
    trigger_date: "",
  }

  // eslint-disable-next-line no-unused-vars
  const onAddReminder = () => {
    if (reminderList.length < 5) {
      setReminderList([...reminderList, DEFAULT_REMINDER])
    }
  }

  const handleStartDate = e => {
    setStartDate(e.target.value)
  }

  const handleEndDate = e => {
    setEndDate(e.target.value)
  }

  const applyStatsFilter = () => {
    if (!startDate || !endDate) return
    if (endDate < startDate) {
      toast.error("End date should be greater than start date")
      return
    }
    fetchProgramStats(dayjs(startDate).toISOString(), dayjs(endDate).endOf("day").toISOString())
  }

  const onTagAdd = useCallback(
    newTag => {
      setSelectedTag(prev => [...prev, newTag])
      setSuggestions([])
      onSaveTag(newTag.value)
      setChangesDetected(true)
    },
    [onSaveTag]
  )

  const onTagDelete = useCallback(
    index => {
      setSelectedTag(prev => prev.filter((_, i) => i !== index))
      setChangesDetected(true)
    },
    []
  )

  const onInput = useCallback(
    value => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)

      debounceTimeoutRef.current = setTimeout(async () => {
        setIsBusy(true)
        try {
          const tagSuggestions = await fetchTags(value)
          setSuggestions(tagSuggestions)
        } catch (error) {
          console.error(error)
        } finally {
          setIsBusy(false)
        }
      }, 100)
    },
    [fetchTags]
  )

  useEffect(() => () => {
    if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
  }, [])

  const noOptionsText = isBusy && !suggestions.length ? "Loading..." : "No characters found"

  const handleExportUsers = () => {
    exportUsersCsv(baseUrl, programId, currentUser).then(res => {
      if (res.response.status === "ok") {
        toast.success(`Export enqueued successfully`)
      } else {
        toast.error(`Enqueuing failed: ${_.get(res, "response.errors.0.detail")}`)
      }
    }).catch(err => {
      toast.error(`Something went wrong: ${err?.request?.responseText}`)
    })
  }

  if (isLoadingProgram) {
    return (
      <div className="content">
        <div className="container-fluid">
          <div className="form-container">
            <div className="d-flex justify-content-center align-items-center mt-4">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!programDetail?.name) {
    return (
      <div className="content">
        <div className="container-fluid">
          <div className="form-container">
            <h1>Program Not Found</h1>
            <div>Please double check the Program Id is correct</div>
            {/* TO DO: Error page */}
          </div>
        </div>
      </div>
    )
  }

  const totalCalculators = ALLOWED_CALCULATOR_TYPES.length
  const activeCalculatorsCount = (programDetail.calculators || []).filter(calc => calc.status === "active").length
  const demographicTotal = form.length
  const demographicEnabledCount = form.filter(question => question.active).length
  const integrationLabels = { learn_world: "LearnWorlds", kajabi: "Kajabi" }
  const integrationLabel = integrationLabels[integrationType] || "—"
  const isActive = (programDetail.status || "").toLowerCase() === "active" || programDetail.active === true
  const lastUpdatedPretty = programDetail.updated_at ? dayjs(programDetail.updated_at).format("DD/MM/YYYY hh:mm A") : null

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="form-container program-edit">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={`/backend/organisations/${programDetail.organisation_id}`}>{programDetail.organisation_name}</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/backend/units/${programDetail.unit_id}`}>{programDetail.unit_name}</Link>
              </li>
              <li aria-current="page" className="breadcrumb-item active">{`${programDetail.name}`}</li>
            </ol>
          </nav>

          <div className="program-edit-header">
            <div>
              <h1>Edit Program</h1>
              <div className="program-edit-subtitle">Update your program details, settings and configuration.</div>
              <div className="program-edit-meta">
                {programDetail.status || programDetail.active !== undefined ? (
                  <span className={`status-badge ${isActive ? "" : "inactive"}`}>
                    <span className="status-dot" />
                    {`Status: ${isActive ? "Active" : "Inactive"}`}
                  </span>
                ) : null}
                {lastUpdatedPretty ? (
                  <span>
                    Last updated:
                    {" "}
                    {lastUpdatedPretty}
                    {programDetail.updated_by ? ` by ${programDetail.updated_by}` : ""}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="program-edit-actions">
              <Link
                className="btn btn-outline-main btn-icon"
                to={`/backend/programs/preview/${programId}`}>
                <FaEye />
                Preview Program
              </Link>
              <button
                className="btn btn-main btn-icon"
                disabled={!changesDetected}
                onClick={onSubmit}
                type="button">
                <FaSave />
                Save Changes
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-8">
              <div className="card-section">
                <div className="card">
                  <div className="card-header">
                    <h5>
                      <FaInfoCircle />
                      Program Details
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="form-group">
                      <label htmlFor="program-name">Program name</label>
                      <input className="form-control" name="name" type="text" value={programDetail.name || ""} onChange={onChange} id="program-name" autoComplete="off" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="offer-redirect-url">
                        Offer Redirect Url
                        {integrationType === "learn_world" ? <span className="text-danger"> *</span> : null}
                      </label>
                      <input id="offer-redirect-url" className="form-control" name="offer_redirect_url" type="text" value={programDetail.offer_redirect_url || ""} onChange={onChange} />
                      {integrationType === "learn_world" && !programDetail.offer_redirect_url?.trim() ? (
                        <p className="mt-2 text-danger">Offer Redirect Url is required for the Learn Worlds integration</p>
                  ) : null}
                    </div>
                    <div className="form-group">
                      <label htmlFor="program-header">Header</label>
                      <input className="form-control" name="header" type="text" value={programDetail.header || ""} onChange={onChange} id="program-header" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="body">Body</label>
                      <ReactQuill
                        id="body"
                        theme="snow"
                        value={programDetail.body || ""}
                        onChange={onBodyChange}
                        modules={BODY_EDITOR_MODULES}
                        formats={BODY_EDITOR_FORMATS} />
                    </div>
                    <div className="form-group">
                      <div>Choose Integration:</div>
                      <div className="integration-toggle">
                        <label className={`btn ${integrationType === "learn_world" ? "active" : ""}`} htmlFor="learn_world">
                          <input type="radio" name="integration_type" value="learn_world" checked={integrationType === "learn_world"} onChange={onChange} id="learn_world" />
                          <FaCheckCircle />
                          Learn Worlds
                        </label>
                        <label className={`btn ${integrationType === "kajabi" ? "active" : ""}`} htmlFor="kajabi">
                          <input type="radio" name="integration_type" value="kajabi" checked={integrationType === "kajabi"} onChange={onChange} id="kajabi" />
                          <FaCheckCircle />
                          Kajabi
                        </label>
                      </div>
                    </div>

                    {integrationType === "learn_world" ? (
                      <div>
                        <div className="form-group">
                          <label htmlFor="learn_world_product_id">Select course or bundle</label>
                          <select
                            id="learn_world_product_id"
                            className="form-control"
                            name="learn_world_product_id"
                            value={programDetail.learn_world_product_id || ""}
                            onChange={onChange}>
                            <option value="">Select a course or bundle</option>
                            {learnWorldProducts?.map(group => (
                              <React.Fragment key={group.product_type}>
                                <option disabled>----------</option>
                                <optgroup label={group.product_type} key={group.product_type}>
                                  {group.products.map(product => (
                                    <option key={product.id} value={product.product_id} disabled={product.state === "draft"}>
                                      {product.title}
                                      {product.state === "draft" ? " (draft)" : ""}
                                    </option>
                                  ))}
                                </optgroup>
                              </React.Fragment>
                        ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor="tags">Tags</label>
                          <ReactTags
                            allowNew
                            labelText="Tags"
                            selected={selectedTag}
                            onAdd={onTagAdd}
                            onDelete={onTagDelete}
                            suggestions={suggestions}
                            onInput={onInput}
                            noOptionsText={noOptionsText} />
                        </div>
                      </div>
                    ) : null}

                    {integrationType === "kajabi" ? (
                      <div className="form-group">
                        <label htmlFor="webhook-url">Kajabi Webhook Url</label>
                        <input id="webhook-url" className="form-control" name="kajabi_offer_webhook_url" type="text" value={programDetail.kajabi_offer_webhook_url || ""} onChange={onChange} />
                      </div>
                    ) : null}
                    {/* TODO Linking code later */}
                    <CopyableField
                      label="Unique Code"
                      value={programDetail.unique_id}
                      variant="copy-field-code"
                      onCopy={copyToClipboard} />
                    <CopyableField
                      label="Unique Sign Up URL"
                      value={programDetail.unique_signup_url}
                      variant="copy-field-url"
                      onCopy={copyToClipboard} />
                    <div className="section-cta">
                      <button
                        className="btn btn-main btn-icon"
                        disabled={!changesDetected}
                        onClick={onSubmit}
                        type="button">
                        <FaSave />
                        Save Details
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-header">
                    <h5>
                      <FaChartBar />
                      Program Statistics
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="stats-toolbar">
                      <div className="stats-filters">
                        <div className="form-group stats-date">
                          <label htmlFor="start-date">From Date</label>
                          <input
                            id="start-date"
                            defaultValue={minDate}
                            onChange={handleStartDate}
                            max={today}
                            min={minDate}
                            className="form-control"
                            type="date" />
                        </div>
                        <div className="form-group stats-date">
                          <label htmlFor="end-date">To Date</label>
                          <input
                            id="end-date"
                            defaultValue={today}
                            onChange={handleEndDate}
                            max={today}
                            min={minDate}
                            className="form-control"
                            type="date" />
                        </div>
                        <button className="btn btn-outline-main btn-sm" onClick={applyStatsFilter} type="button">
                          Apply Filter
                        </button>
                      </div>
                      <button className="btn btn-outline-main btn-sm btn-icon" onClick={handleExportUsers} type="button">
                        <FaDownload />
                        Export Statistics (CSV)
                      </button>
                    </div>
                    <table className="table stats-table">
                      <thead>
                        <tr>
                          <th scope="col">Calculator</th>
                          <th scope="col">Attempts</th>
                          <th scope="col">Once (=1)</th>
                          <th scope="col">
                            Once (
                            {">"}
                            1)
                          </th>
                          <th scope="col">Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calculatorStats.map(stat => {
                          const attempts = stat.completions || 0
                          const completed = stat.completed_at_least_once || 0
                          const pct = attempts > 0 ? Math.round((completed / attempts) * 100) : 0
                          return (
                            <tr key={stat.id}>
                              <td className="align-middle"><b>{stat.calculator_type}</b></td>
                              <td className="align-middle">{stat.completions}</td>
                              <td className="align-middle">{stat.completed_once}</td>
                              <td className="align-middle">{stat.completed_more_than_once}</td>
                              <td className="align-middle">
                                <div className="progress-cell">
                                  <span className="progress-pct">{`${pct}%`}</span>
                                  <div className="progress-track">
                                    <div className="progress-fill" style={{ width: `${pct}%` }} />
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                        {!calculatorStats.length && (
                          <tr>
                            <td className="text-muted" colSpan={5}>No statistics for the selected period.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className="stats-footer">
                      {`Showing ${calculatorStats.length} of ${ALLOWED_CALCULATOR_TYPES.length} calculators`}
                    </div>
                  </div>
                </div>
                <div className="card calculators-card">
                  <div className="card-header with-subtitle">
                    <h5>
                      <FaCalculator />
                      Calculators
                    </h5>
                  </div>
                  <div className="card-body">
                    <table className="table calculators-table">
                      <thead>
                        <tr>
                          <th scope="col">Calculator</th>
                          <th scope="col">Status</th>
                          <th scope="col">Attempts</th>
                          <th scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ALLOWED_CALCULATOR_TYPES.map(calculatorType => {
                          const label = calculatorTypeLabel(calculatorType)
                          const statRow = calculatorStats.find(
                            s => s.calculator_type === convertSnakeToTitle(calculatorType) || s.calculator_type === calculatorType
                          )
                          return (
                            <tr key={calculatorType}>
                              <td className="align-middle">{label}</td>
                              <SwitchWithInflight
                                attempts={statRow?.completions || 0}
                                baseUrl={baseUrl}
                                currentUser={currentUser}
                                calculatorType={calculatorType}
                                calculatorDetail={programDetail.calculators.find(calc => calc.calculator_type === calculatorType)}
                                programDetail={programDetail}
                                programId={programId} />
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    <div className="card-footnote">Enabled calculators will be available to users in the program.</div>
                  </div>
                </div>
                <div className="row pulse-demographic-row">
                  <div className="col-md-6">
                    <div className="card">
                      <div className="card-header">
                        <h5>
                          <FaHeartbeat />
                          Pulse Check
                        </h5>
                      </div>
                      <div className="card-body">
                        {customTemplates && (
                        <>
                          <div className="form-group">
                            <label htmlFor="pulse-check">Pulse Check</label>
                            <select
                              id="pulse-check"
                              className="form-control"
                              name="question_type"
                              onChange={onUpdatePulseCalculator}
                              value={selectedCustomTemplate?.name}>
                              <option value="">Select a Pulse Check</option>
                              {customTemplates.map(template => (
                                <option key={template.name} value={template.name}>{template.name}</option>
                        ))}
                            </select>
                          </div>
                          {selectedCustomTemplate && (
                          <p>
                            Current Live Calculator:
                            {" "}
                            <strong>{selectedCustomTemplate?.name}</strong>
                          </p>
                    )}
                          <div className="form-group">
                            <label htmlFor="publish-date">Publish Date</label>
                            <input
                              id="publish-date"
                              className="form-control"
                              onChange={onUpdatePublishDate}
                              name="published_date"
                              type="date"
                              value={dayjs(calculatorPublishDate, "YYYY-MM-DD").format("YYYY-MM-DD")} />
                            {selectedCustomTemplate?.status === "active" && (
                            <p>
                              Current Live Calculator published at:
                              {" "}
                              <strong>{selectedCustomTemplate?.published_date_pretty}</strong>
                            </p>
                      )}
                          </div>
                        </>
                )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mt-4 mt-md-0">
                    <div className="card">
                      <div className="card-header">
                        <h5>
                          <FaUsers />
                          Demographic Questions
                        </h5>
                      </div>
                      <div className="card-body">
                        <table className="table">
                          <tbody>
                            <tr>
                              <td>Age</td>
                              <td className="text-right">
                                <div className="custom-control custom-switch">
                                  <input
                                    className="custom-control-input"
                                    id="customSwitch1"
                                    type="checkbox"
                                    onChange={onSwitchClick}
                                    name={0}
                                    checked={form[0]?.active || false} />
                                  <label className="custom-control-label" htmlFor="customSwitch1"><span className="sr-only">Toggle Age</span></label>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>Job Level</td>
                              <td className="text-right">
                                <div className="custom-control custom-switch">
                                  <input
                                    className="custom-control-input"
                                    id="customSwitch2"
                                    type="checkbox"
                                    onChange={onSwitchClick}
                                    name={1}
                                    checked={form[1]?.active || false} />
                                  <label className="custom-control-label" htmlFor="customSwitch2"><span className="sr-only">Toggle Job Level</span></label>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>Sex</td>
                              <td className="text-right">
                                <div className="custom-control custom-switch">
                                  <input
                                    className="custom-control-input"
                                    id="customSwitch3"
                                    type="checkbox"
                                    onChange={onSwitchClick}
                                    name={2}
                                    checked={form[2]?.active || false} />
                                  <label className="custom-control-label" htmlFor="customSwitch3"><span className="sr-only">Toggle Sex</span></label>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>Language at home</td>
                              <td className="text-right">
                                <div className="custom-control custom-switch">
                                  <input
                                    className="custom-control-input"
                                    id="customSwitch4"
                                    type="checkbox"
                                    onChange={onSwitchClick}
                                    name={3}
                                    checked={form[3]?.active || false} />
                                  <label className="custom-control-label" htmlFor="customSwitch4"><span className="sr-only">Toggle Language at home</span></label>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>Aboriginal/Torres Strait Islander</td>
                              <td className="text-right">
                                <div className="custom-control custom-switch">
                                  <input
                                    className="custom-control-input"
                                    id="customSwitch5"
                                    type="checkbox"
                                    onChange={onSwitchClick}
                                    name={4}
                                    checked={form[4]?.active || false} />
                                  <label className="custom-control-label" htmlFor="customSwitch5"><span className="sr-only">Toggle Aboriginal/Torres Strait Islander</span></label>
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td>Country of Birth</td>
                              <td className="text-right">
                                <div className="custom-control custom-switch">
                                  <input
                                    className="custom-control-input"
                                    id="customSwitch6"
                                    type="checkbox"
                                    onChange={onSwitchClick}
                                    name={5}
                                    checked={form[5]?.active || false} />
                                  <label className="custom-control-label" htmlFor="customSwitch6"><span className="sr-only">Toggle Country of Birth</span></label>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card-section">
                <div className="card summary-card">
                  <div className="card-header">
                    <h5>
                      <FaClipboardList />
                      Program Summary
                    </h5>
                  </div>
                  <div className="card-body">
                    {programDetail.status || programDetail.active !== undefined ? (
                      <div className="summary-row">
                        <span className="summary-label">Program Status</span>
                        <span className="summary-value">
                          <span className={`status-badge ${isActive ? "" : "inactive"}`}>
                            <span className="status-dot" />
                            {isActive ? "Active" : "Inactive"}
                          </span>
                        </span>
                      </div>
                    ) : null}
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaLink />
                        Unique Code
                      </span>
                      <span className="summary-value">
                        {programDetail.unique_id || "—"}
                        {programDetail.unique_id ? (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm ml-2"
                            title="Copy unique code"
                            onClick={() => copyToClipboard(programDetail.unique_id, "Unique Code")}>
                            <FaRegCopy />
                          </button>
                        ) : null}
                      </span>
                    </div>
                    {programDetail.unique_signup_url ? (
                      <div className="summary-row">
                        <span className="summary-label">
                          <FaExternalLinkAlt />
                          Sign Up URL
                        </span>
                        <span className="summary-value">
                          <a href={programDetail.unique_signup_url} target="_blank" rel="noopener noreferrer">View / Copy</a>
                        </span>
                      </div>
                    ) : null}
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaPlug />
                        Integration
                      </span>
                      <span className="summary-value">{integrationLabel}</span>
                    </div>
                    {selectedCustomTemplate?.name ? (
                      <div className="summary-row">
                        <span className="summary-label">
                          <FaHeartbeat />
                          Pulse Check
                        </span>
                        <span className="summary-value">
                          {selectedCustomTemplate?.status === "active"
                            ? <span className="badge-soft">Published</span>
                            : selectedCustomTemplate?.name}
                        </span>
                      </div>
                    ) : null}
                    {selectedCustomTemplate?.published_date_pretty ? (
                      <div className="summary-row">
                        <span className="summary-label">Publish Date</span>
                        <span className="summary-value">{selectedCustomTemplate.published_date_pretty}</span>
                      </div>
                    ) : null}
                    <div className="summary-row">
                      <span className="summary-label">
                        <FaCalculator />
                        Active Calculators
                      </span>
                      <span className="summary-value">{`${activeCalculatorsCount} of ${totalCalculators}`}</span>
                    </div>
                    {demographicTotal ? (
                      <div className="summary-row">
                        <span className="summary-label">
                          <FaUsers />
                          Demographic Questions
                        </span>
                        <span className="summary-value">{`${demographicEnabledCount} of ${demographicTotal}`}</span>
                      </div>
                    ) : null}
                    {lastUpdatedPretty ? (
                      <div className="summary-row">
                        <span className="summary-label">
                          <FaHistory />
                          Last Updated
                        </span>
                        <span className="summary-value">
                          {lastUpdatedPretty}
                          {programDetail.updated_by ? <div>{`by ${programDetail.updated_by}`}</div> : null}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="card">
                  <div className="card-header">
                    <h5>
                      <FaBolt />
                      Quick Actions
                    </h5>
                  </div>
                  <div className="card-body quick-actions">
                    <Link className="quick-action" to={`/backend/programs/preview/${programId}`}>
                      <span className="quick-action-label">
                        <FaEye />
                        Preview Program
                      </span>
                      <FaChevronRight />
                    </Link>
                    {programDetail.unique_signup_url ? (
                      <a className="quick-action" href={programDetail.unique_signup_url} target="_blank" rel="noopener noreferrer">
                        <span className="quick-action-label">
                          <FaExternalLinkAlt />
                          View Sign Up Page
                        </span>
                        <FaChevronRight />
                      </a>
                    ) : null}
                    <button className="quick-action" type="button" onClick={handleExportUsers}>
                      <span className="quick-action-label">
                        <FaDownload />
                        Export All Data (CSV)
                      </span>
                      <FaChevronRight />
                    </button>
                    <Link className="quick-action" to={`/backend/activity-logs?program_id=${programId}`}>
                      <span className="quick-action-label">
                        <FaHistory />
                        View Activity Log
                      </span>
                      <FaChevronRight />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {changesDetected ? (
            <div className="form-container-cta fixed-bottom">
              <button
                className="btn btn-main btn-icon"
                disabled={!changesDetected}
                onClick={onSubmit}
                type="button">
                <FaSave />
                Save Changes
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default ProgramDetailPage
